import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

export const SUPPORTED_LOCALES = ['cs', 'en', 'de'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

const FALLBACK: SupportedLocale = 'cs';

// Dynamické importy → Vite vyrobí z každého locale samostatný chunk.
// Stáhne se jen aktivní jazyk (+ fallback), ne všechny tři najednou jako dřív.
const loaders: Record<SupportedLocale, () => Promise<{ default: Record<string, unknown> }>> = {
  cs: () => import('./cs.json'),
  en: () => import('./en.json'),
  de: () => import('./de.json'),
};

async function loadLocale(lang: SupportedLocale): Promise<void> {
  if (i18n.hasResourceBundle(lang, 'translation')) return;
  const mod = await loaders[lang]();
  i18n.addResourceBundle(lang, 'translation', mod.default, true, true);
}

/** Zúží libovolný BCP-47 tag (`en-US`) na podporovaný locale; neznámé → fallback. Exportováno pro testy. */
export function normalizeLocale(lng: string | undefined): SupportedLocale {
  const base = (lng ?? FALLBACK).split('-')[0];
  return (SUPPORTED_LOCALES as readonly string[]).includes(base) ? (base as SupportedLocale) : FALLBACK;
}

/**
 * Inicializuje i18next BEZ staticky nabundlovaných resources a doloaduje jen detekovaný
 * jazyk (+ fallback). Voláno z main.tsx PŘED renderem, aby nebliklo zobrazení klíčů.
 */
export async function initI18n(): Promise<void> {
  await i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {}, // prázdné — bundly se přidávají lazy přes addResourceBundle
      partialBundledLanguages: true, // povolí start bez předem načtených resources
      fallbackLng: FALLBACK,
      supportedLngs: SUPPORTED_LOCALES as unknown as string[],
      interpolation: { escapeValue: false },
      // Bundly preloadujeme ručně níže → Suspense není potřeba (a nehrozí flash).
      react: { useSuspense: false },
      detection: {
        order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
        lookupQuerystring: 'lang',
        lookupLocalStorage: 'ks_lang',
        caches: ['localStorage'],
      },
    });

  const active = normalizeLocale(i18n.resolvedLanguage ?? i18n.language);
  await Promise.all(active === FALLBACK ? [loadLocale(FALLBACK)] : [loadLocale(active), loadLocale(FALLBACK)]);
  document.documentElement.lang = active;
}

export default i18n;

export async function changeLanguage(lang: SupportedLocale): Promise<void> {
  await loadLocale(lang); // chunk musí být v paměti dřív, než přepneme
  await i18n.changeLanguage(lang);
  document.documentElement.lang = lang;
}
